/**
 * HeaderDrawer — fixes the missing JS class for <header-drawer> element.
 *
 * The Cashmere theme's mobile menu uses <header-drawer> as a wrapper around
 * a native <details>/<summary>, but never registered a custom element class
 * for it. The CSS rule `.js details[open].menu-opening > .menu-drawer`
 * requires the `menu-opening` class to slide the panel in. Without a JS
 * class, that class was never added → drawer panel stays hidden offscreen.
 *
 * This class restores that behaviour.
 */
class HeaderDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;
    this.mainSummary = this.mainDetailsToggle.querySelector('summary');
    this.breakpoint = this.dataset.breakpoint || 'tablet';
    this._closeTimer = null;
    this.bindEvents();
  }

  bindEvents() {
    if (this.mainSummary) {
      this.mainSummary.addEventListener('click', this.onMainSummaryClick.bind(this));
    }

    this.querySelectorAll('details details > summary').forEach((s) => {
      s.addEventListener('click', this.onSubmenuClick.bind(this));
    });

    this.querySelectorAll('.menu-drawer__close-btn, .menu-drawer__close-menu-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMenuDrawer();
      });
    });

    this.querySelectorAll('.menu-drawer__close-button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const details = e.currentTarget.closest('details');
        if (details) this.closeSubmenu(details);
      });
    });

    this.addEventListener('keyup', (e) => {
      if (e.code !== 'Escape' && e.key !== 'Escape') return;
      const openInside = e.target.closest('details[open]');
      if (openInside && openInside !== this.mainDetailsToggle) {
        this.closeSubmenu(openInside);
      } else if (this.mainDetailsToggle.hasAttribute('open')) {
        this.closeMenuDrawer();
      }
    });
  }

  onMainSummaryClick(e) {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      e.preventDefault();
      this.closeMenuDrawer();
    } else {
      requestAnimationFrame(() => this.openMenuDrawer());
    }
  }

  openMenuDrawer() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    this.mainDetailsToggle.classList.add('menu-opening');
    document.body.classList.add('overflow-hidden-' + this.breakpoint);
    if (this.mainSummary) this.mainSummary.setAttribute('aria-expanded', 'true');
  }

  closeMenuDrawer() {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((d) => {
      d.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((el) => {
      el.classList.remove('submenu-open');
    });
    document.body.classList.remove('overflow-hidden-' + this.breakpoint);
    if (this.mainSummary) this.mainSummary.setAttribute('aria-expanded', 'false');
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      this.mainDetailsToggle.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
      this.mainDetailsToggle.removeAttribute('open');
      this._closeTimer = null;
    }, 400);
  }

  onSubmenuClick(e) {
    const summary = e.currentTarget;
    const details = summary.parentNode;
    const parentSubmenu = details.closest('.has-submenu');
    setTimeout(() => {
      details.classList.add('menu-opening');
      summary.setAttribute('aria-expanded', 'true');
      if (parentSubmenu) parentSubmenu.classList.add('submenu-open');
    }, 100);
  }

  closeSubmenu(details) {
    if (!details) return;
    const parentSubmenu = details.closest('.submenu-open');
    if (parentSubmenu) parentSubmenu.classList.remove('submenu-open');
    details.classList.remove('menu-opening');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    setTimeout(() => details.removeAttribute('open'), 400);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
